/**
 * FailedPage - Payment failed or cancelled
 * Route: /order/:sessionId/failed
 */

import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBrandTheme } from "@/lib/brand-theme";
import { BrandLogo } from "@/components/BrandLogo";
import { Card } from "@/components/Card";
import { Button } from "@/components/Button";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { checkoutEntryPath, returnToExpressCart } from "@/lib/express-cart/device";

type FailureReason = "cancelled" | "declined" | "timeout" | "error" | "unknown";

const FAILURE_MESSAGES: Record<FailureReason, { title: string; message: string }> = {
  cancelled: {
    title: "Payment Cancelled",
    message: "You cancelled the payment process. Your order has not been placed.",
  },
  declined: {
    title: "Payment Declined",
    message:
      "Your payment was declined by your bank. Please try again with a different payment method.",
  },
  timeout: {
    title: "Payment Timeout",
    message: "The payment session expired. Please try again.",
  },
  error: {
    title: "Payment Error",
    message: "There was an error processing your payment. Please try again.",
  },
  unknown: {
    title: "Payment Failed",
    message: "Something went wrong with your payment. Please try again.",
  },
};

// Secure checkout badge
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div>
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-(--color-surface-secondary) transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-(--color-text-secondary)"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

export function FailedPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");
  const reason = (searchParams.get("reason") as FailureReason) || "unknown";
  const errorCode = searchParams.get("error_code");
  const { theme } = useBrandTheme();
  const { resolvedTheme } = useTheme();

  const failureInfo = FAILURE_MESSAGES[reason] || FAILURE_MESSAGES.unknown;

  const handleRetryPayment = () => {
    if (orderId) {
      navigate(`/order/${sessionId}/pay?order_id=${orderId}`);
    } else if (sessionId) {
      navigate(checkoutEntryPath(sessionId));
    }
  };

  // "Return to Cart": the standalone cart for this session.
  const handleStartOver = () => {
    if (returnToExpressCart(sessionId)) return;
    navigate(`/order/${sessionId}`);
  };

  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between gap-5">
      {/* Header */}
      <div>
        <header className="bg-(--color-surface-primary) border-b border-(--color-surface-border-light)">
          <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
            <div className="flex items-center justify-between">
              <BrandLogo brand={theme} />
            </div>

            <SecureBadge />
          </div>
        </header>

        <main className="max-w-2xl mx-auto px-4 py-8">
          {/* Failure Message */}
          <div className="text-center mb-8">
            <div className="mx-auto w-16 h-16 bg-[var(--color-error-light)] rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-(--color-error)"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </div>

            <h1 className="text-[24px] font-semibold text-(--color-text-primary) mb-2">
              {failureInfo.title}
            </h1>
            <p className="text-[14px] text-(--color-text-secondary)">{failureInfo.message}</p>
          </div>

          {/* Error Details */}
          {errorCode && (
            <Card className="mb-6 bg-[var(--color-error-light)] border-[var(--color-error)]">
              <div className="text-[14px] text-(--color-error)">
                <p className="font-medium">Error Code: {errorCode}</p>
                <p className="text-(--color-error) mt-1 opacity-80">
                  If this problem persists, please contact support with this error code.
                </p>
              </div>
            </Card>
          )}

          {/* Actions */}
          <Card className="mb-6">
            <h2 className="text-[16px] font-semibold text-(--color-text-primary) mb-4">
              What would you like to do?
            </h2>

            <div className="space-y-3">
              {orderId && (
                <Button onClick={handleRetryPayment} fullWidth size="lg">
                  <svg
                    className="w-5 h-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Try Payment Again
                </Button>
              )}

              <Button onClick={handleStartOver} variant="secondary" fullWidth size="lg">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z"
                  />
                </svg>
                Return to Cart
              </Button>
            </div>
          </Card>

          {/* Troubleshooting Tips */}
          <Card className="bg-[var(--color-info-light)] border-[var(--color-info)]">
            <div className="flex items-start gap-3">
              <svg
                className="w-5 h-5 text-[var(--color-info)] flex-shrink-0 mt-0.5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                />
              </svg>
              <div className="text-[14px] text-(--color-text-primary)">
                <p className="font-medium mb-2">Troubleshooting Tips</p>
                <ul className="space-y-1.5 text-(--color-text-secondary)">
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--color-info)] mt-1">•</span>
                    <span>Check that your card has sufficient funds</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--color-info)] mt-1">•</span>
                    <span>Ensure your card is enabled for online payments</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--color-info)] mt-1">•</span>
                    <span>Try a different payment method</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-[var(--color-info)] mt-1">•</span>
                    <span>Check with your bank if payments are being blocked</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Support Contact */}
          <div className="mt-8 text-center text-[13px] text-(--color-text-muted)">
            <p>
              Need help?{" "}
              <a
                href="#"
                className="text-[var(--color-secondary,var(--color-primary))] hover:text-[var(--color-secondary-hover,var(--color-primary-hover))] font-medium"
              >
                Contact Support
              </a>
            </p>
          </div>
        </main>
      </div>

      <footer className="flex border-t border-(--color-surface-border-light) p-4 z-50 items-center justify-center gap-1.5 text-xs text-(--color-text-muted)">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5 mb-1" />
        ) : (
          <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5 mb-1" />
        )}
      </footer>
    </div>
  );
}
